import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "next-i18next";
import { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import { IFilterTag } from "src/api/server/extended-search.types";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import type { ITagIconTag } from "src/components-mui/base/table-scrollable/components/entity/tag-icon.types";
import { TableEmptyState } from "src/components-mui/base/table-scrollable/components/table-empty-state";
import { MuiTableScrollable } from "src/components-mui/base/table-scrollable/table-scrollable";
import { getTableDataForLoading } from "src/components-mui/base/table-scrollable/utils";
import { ModulePrimaryHeadline } from "src/components-mui/common/module-headlines";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { Container } from "src/components-mui/layout/container";
import { WIKIFOLIO_TABLE_FIRST_COLUMN_WIDTH } from "src/components-mui/modules/wikifolio-table/consts";
import { makeWikifolioTableColumns } from "src/components-mui/modules/wikifolio-table/helpers/make-wikifolio-table-columns";
import { makeWikifolioTableLoadingColumns } from "src/components-mui/modules/wikifolio-table/helpers/make-wikifolio-table-loading-columns";
import { useWikifolioTableModuleSearch } from "src/components-mui/modules/wikifolio-table/hooks/use-wikifolio-table-module-search";
import { ITableRanking } from "src/components-mui/modules/wikifolio-table/types";
import { ILink, ITooltip } from "src/types/common";
import { getFirstSortableFigureSortingState } from "src/utils/table/get-first-sortable-figure-sorting-state";

const DATA_TEST_ID = "wikifolio-table-module";

export interface IWikifolioTableFigure {
    id: string;
    label: string;
    fullLabel: string;
    orderDescending: boolean;
    tooltip: ITooltip;
}

export type IWikifolioTableTag = ITagIconTag;

interface IWikifolioTableSearchData {
    extendedSearchParameter: string;
    numberOfWikifolios: number;
    rankings: ITableRanking[];
    tags: IFilterTag[];
}

export interface IWikifolioTableModuleProps {
    title?: string;
    description?: string;
    defaultFigures?: IWikifolioTableFigure[];
    wikifolioIsins: string[];
    tags?: IWikifolioTableTag[];
    bottomLink?: ILink;
    searchData?: IWikifolioTableSearchData;
}

export const WikifolioTableModule = ({
    title,
    description,
    bottomLink,
    tags,
    defaultFigures,
    wikifolioIsins,
    searchData,
}: IWikifolioTableModuleProps) => {
    const { t } = useTranslation("common");
    const [sorting, setSorting] = useState<SortingState>(getFirstSortableFigureSortingState(defaultFigures ?? [], "trader"));
    const { results, isLoading } = useWikifolioTableModuleSearch({
        wikifolioIsins,
        defaultFigures: defaultFigures,
        searchQuery: searchData?.extendedSearchParameter,
        allRankings: searchData?.rankings,
        tags: searchData?.tags,
        limit: searchData?.numberOfWikifolios,
    });

    const columns = useMemo(() => makeWikifolioTableColumns(defaultFigures, tags), [defaultFigures, tags]);
    const loadingColumns = useMemo(() => makeWikifolioTableLoadingColumns({ figures: defaultFigures || [], sorting: true }), [defaultFigures]);
    const hasNoData = !isLoading && (!results || results.length === 0);

    return (
        <React.StrictMode>
            <Container data-test-id={DATA_TEST_ID}>
                {title && <ModulePrimaryHeadline sx={{ marginBottom: 2 }}>{title}</ModulePrimaryHeadline>}
                {description && <SimpleRichText text={description} fontSize="lg" />}
                <Box
                    sx={{
                        mt: title || description ? 5 : 0,
                    }}
                >
                    {hasNoData ? (
                        <TableEmptyState
                            columns={() =>
                                makeWikifolioTableLoadingColumns({
                                    figures: defaultFigures || [],
                                    disableAnimation: true,
                                    skeletonBackgroundColor: "gray.50",
                                })
                            }
                            emptyStateText={t("table.no-data")}
                        />
                    ) : (
                        <MuiTableScrollable
                            data={(isLoading ? getTableDataForLoading(6, 0, 6) : results) as IExtendedSearchItem[]}
                            columns={isLoading ? loadingColumns : columns}
                            sorting={sorting}
                            onSortingChange={setSorting}
                            firstColumnWidth={WIKIFOLIO_TABLE_FIRST_COLUMN_WIDTH}
                        />
                    )}
                </Box>
                {bottomLink && (
                    <Box
                        sx={{
                            marginTop: 3,
                            textAlign: "right",
                        }}
                    >
                        <ArrowLink sx={{ fontSize: "md" }} href={bottomLink.url} target={bottomLink.target}>
                            {bottomLink.name}
                        </ArrowLink>
                    </Box>
                )}
            </Container>
        </React.StrictMode>
    );
};
